import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';

const useQueryString = <T>() => {
  const { search, pathname } = useLocation();
  const navigate = useNavigate();
  const query = useMemo(() => queryString.parse(search), [search]);

  const onAddQueryString = useCallback(
    (value: Partial<T>) => {
      const newQuery = { ...query, ...value };
      navigate(`${pathname}?${queryString.stringify(newQuery)}`);
    },
    [query, history, pathname]
  );

  const onUpdateQueryString = useCallback(
    (value: Partial<T>) => {
      const newQuery = { ...query, ...value };
      navigate(`${pathname}?${queryString.stringify(newQuery)}`, {
        replace: true
      });
    },
    [query, history, pathname]
  );

  const onReplaceQueryString = useCallback(
    (value: Partial<T>) => {
      const emptyQuery = queryString.parse('');
      const newQuery = { ...emptyQuery, ...value };
      navigate(`${pathname}?${queryString.stringify(newQuery)}`);
    },
    [history, pathname]
  );

  const onMoveQueryString = useCallback(
    (value: Partial<T>) => {
      return navigate(`${pathname}?${queryString.stringify(value as any)}`);
    },
    [history, pathname]
  );

  const getQueryString = useCallback((value: Partial<T>) => {
    return queryString.stringify(value as any);
  }, []);

  return {
    pathname,
    query: query as unknown as T,
    onAddQueryString,
    onUpdateQueryString,
    onMoveQueryString,
    onReplaceQueryString,
    getQueryString
  };
};

export default useQueryString;
